/* eslint-disable react/jsx-props-no-spreading */
import { ConfigProvider, theme, Button } from 'antd'

import { colorTheme } from 'assets/colorTheme'

const { gray, blue, green } = colorTheme
const { useToken } = theme

/**
 * size가 props로 들어 왔을 때
 * size별 styling
 * @param sizeStyle
 * @param type
 * @param override
 * @param active 필터 버튼
 * @param filterColor 필터 컬러
 * @returns {*|{padding: string, alignItems: string, color: string|string, textAlign: string, display: string, fontStyle: string, justifyContent: string, fontFamily: string, gap: string, fontSize: string, lineHeight: string, fontWeight: number, height: string}}
 */
const getSizeStyle = (sizeStyle, type, override, active, filterColor) => {
    /**
     * 필터버튼에서 사용
     * @param color
     * @param activeStatus
     * @returns {string}
     */
    const getFilterColor = (color, activeStatus) => {
        if (activeStatus) {
            if (color === 'yellow') {
                return '#F9A71A'
            }
            if (color === 'green') {
                return '#00A886'
            }
            if (color === 'blue') {
                return '#2D8DD2'
            }
            if (color === 'navy') {
                return '#024383'
            }
            if (color === 'darkgray') {
                return '#7A7A7A'
            }
        }
        return ''
    }

    // 공통 style
    const textStyle = {
        color: type === 'primary' ? 'var(--gray-white, #FFF)' : `${override}`,
        backgroundColor: getFilterColor(filterColor, active),
        textAlign: 'center',
        fontFamily: 'Noto Sans KR',
        display: 'inline-flex',
        fontStyle: 'normal',
        fontWeight: 700,
        justifyContent: 'center',
        alignItems: 'center',
        gap: '10px',
        boxShadow: 'none',
    }
    const sizes = {
        large: {
            ...textStyle,
            height: '60px',
            padding: '19px 64px',
            fontSize: '18px',
            lineHeight: '22px',
        },
        middle: {
            ...textStyle,
            height: '48px',
            padding: '14px 32px',
            fontSize: '16px',
            lineHeight: '20px',
        },
        small: {
            ...textStyle,
            height: '36px',
            padding: '9px 28px',
            fontSize: '14px',
            lineHeight: '18px',
        },
        filter: {
            ...textStyle,
            // 선택 상태를 인라인에서 결정한다. 예전엔 textStyle 의 700 이 모든 필터 버튼에 붙고
            // 전역 .btn_wrap button 의 `font-weight: 400 !important` 가 그걸 눌러서,
            // 선택된 버튼도 절대 굵어지지 않았다(!important 는 인라인도 이김).
            fontWeight: active ? 700 : 400,
            height: '48px',
            padding: '0px 12px',
            alignItems: 'center',

            border: '1px solid var(--gray-300, #E5E5E5)',
            color: '#7a7a7a', // 필터 비활성 폰트색 (연회색 #c2c2c2 → 진회색, Disabled 오인 방지)
            backgroundColor: 'transparent', // 활성 시 색 배경 채움 제거 (테두리 방식 통일)
            minWidth: '88px', // 체크 아이콘 제거 후에도 버튼 크기 유지 (텍스트 중앙 정렬)
        },
        download: {
            ...textStyle,
            padding: '20px',
            alignItems: 'center',
            fontSize: '16px',
        },
    }

    return sizes[sizeStyle] || sizes.middle // 기본값으로 'middle' 스타일을 사용
}

/**
 * hover 컬러값 1단계 낮추는 함수
 * @param overrideColor
 * @returns {*}
 */
const getHoverColor = overrideColor => {
    const colorMapping = {
        [blue['900']]: blue['800'],
        [green['900']]: green['800'],
        [green['400']]: green['300'],
    }

    return colorMapping[overrideColor] || blue['800']
}

/**
 *
 * @param children
 * @param override
 * @param size
 * @param disabled
 * @param active 필터 버튼 active
 * @param filterColor 필터 버튼 color
 * @param icon
 * @param htmlType
 * @param props
 * @returns {JSX.Element}
 * @constructor
 */
const ColorButton = ({ children, override, size, disabled, active, filterColor, icon, htmlType, ...props }) => {
    const { type, onClick, style: userStyle, ...rest } = props
    const { token } = useToken() // AntD token
    const overrideColor = token[override] || override

    const baseColor = getHoverColor(overrideColor)

    // AntD theme token 주입
    const modifiedTheme = {
        token: {
            fontSize: 14,
            borderRadius: 4,
            colorPrimary: overrideColor || token.colorPrimary,
            colorTextBase: overrideColor || token.colorPrimary,
            colorBorder: disabled ? gray['200'] : overrideColor || token.colorPrimary,
            colorBgTextHover: baseColor,
        },
        // algorithm,
    }

    const baseStyle = getSizeStyle(size, type, override, active, filterColor)
    // primary 배경/테두리색을 인라인으로 명시 → antd CSS-in-JS 주입이 늦을 때 버튼이 브라우저 기본 '회색'으로
    // 떨어지던 문제(꽤 자주 발생) 방지. (filterColor 버튼은 제외 — 자체 배경 로직 사용)
    const primaryBg = type === 'primary' && !filterColor && !disabled ? overrideColor || token.colorPrimary : null
    // disabled primary: 앱의 미선택/비활성 언어(연한 아웃라인)로 통일 → 연한 배경 + 회색 테두리/글자
    // (아이콘은 currentColor라 이 글자색을 따라 회색으로 표시됨)
    const disabledStyle =
        disabled && type === 'primary'
            ? { backgroundColor: gray['50'], borderColor: gray['300'], color: gray['400'] }
            : {}
    const mergedStyle = {
        ...baseStyle,
        ...(primaryBg ? { backgroundColor: primaryBg, borderColor: primaryBg } : {}),
        ...disabledStyle,
        ...(userStyle || {}),
    }

    return (
        <ConfigProvider theme={modifiedTheme}>
            <Button
                {...rest}
                type={type} // ← 빠뜨렸던 부분 복구
                disabled={disabled}
                onClick={onClick}
                htmlType={htmlType === 'submit' ? 'submit' : 'button'}
                style={mergedStyle}
            >
                {children}
                {icon}
            </Button>
        </ConfigProvider>
    )
}

export default ColorButton
